import {
  createContext,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
} from "react";
import type { ButtonHTMLAttributes, HTMLAttributes, KeyboardEvent, ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * `underline` = abas de CONTEÚDO (sublinhado violeta).
 * `segmented` = presets e filtros (Hoje · 7 dias · 30 dias; Todos · Lotando):
 * trilho cinza em cápsula, item selecionado em pílula branca.
 */
export type TabsVariant = "underline" | "segmented";

interface TabsContextValue {
  value: string;
  variant: TabsVariant;
  setValue: (value: string) => void;
  baseId: string;
  orientation: "horizontal" | "vertical";
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabs(component: string): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error(`<${component}> precisa estar dentro de <Tabs>.`);
  return ctx;
}

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  /** Controlado. */
  value?: string;
  /** Não controlado. */
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  orientation?: "horizontal" | "vertical";
  /** Padrão `underline`. `segmented` só faz sentido na horizontal. */
  variant?: TabsVariant;
}

/** Abas acessíveis (roving tabindex, setas/Home/End) em duas aparências. */
export function Tabs({
  value: controlledValue,
  defaultValue,
  onValueChange,
  orientation = "horizontal",
  variant = "underline",
  className,
  children,
  ...props
}: TabsProps) {
  const baseId = useId();
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? "");
  const value = controlledValue ?? uncontrolled;

  const setValue = useCallback(
    (next: string) => {
      if (controlledValue === undefined) setUncontrolled(next);
      onValueChange?.(next);
    },
    [controlledValue, onValueChange],
  );

  return (
    <TabsContext.Provider value={{ value, setValue, baseId, orientation, variant }}>
      <div className={cn("flex flex-col gap-4", className)} {...props}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export interface TabsListProps extends HTMLAttributes<HTMLDivElement> {
  /** Nome da lista de abas para leitor de tela. */
  label: string;
}

export function TabsList({ label, className, children, ...props }: TabsListProps) {
  const { orientation, variant } = useTabs("TabsList");
  const ref = useRef<HTMLDivElement>(null);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const keys =
      orientation === "horizontal"
        ? { prev: "ArrowLeft", next: "ArrowRight" }
        : { prev: "ArrowUp", next: "ArrowDown" };

    if (!["Home", "End", keys.prev, keys.next].includes(event.key)) return;

    const tabs = Array.from(
      ref.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])') ?? [],
    );
    if (tabs.length === 0) return;

    const current = tabs.findIndex((tab) => tab === document.activeElement);
    let nextIndex = current;

    if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = tabs.length - 1;
    else if (event.key === keys.next) nextIndex = (current + 1 + tabs.length) % tabs.length;
    else if (event.key === keys.prev) nextIndex = (current - 1 + tabs.length) % tabs.length;

    const target = tabs[nextIndex];
    if (target) {
      event.preventDefault();
      target.focus();
      target.click();
    }
  };

  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={label}
      aria-orientation={orientation}
      onKeyDown={onKeyDown}
      className={cn(
        variant === "segmented"
          ? "inline-flex max-w-full items-center gap-0.5 self-start overflow-x-auto rounded-pill bg-muted p-1 scrollbar-thin"
          : orientation === "horizontal"
            ? "flex items-stretch gap-5 overflow-x-auto border-b border-border scrollbar-thin"
            : "flex flex-col items-stretch border-l border-border",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export interface TabsTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  /** Contador ao lado do rótulo (ex.: Lotando 4). `0` aparece — é um número medido. */
  count?: ReactNode;
}

export function TabsTrigger({ value, count, className, children, ...props }: TabsTriggerProps) {
  const ctx = useTabs("TabsTrigger");
  const active = ctx.value === value;
  const segmented = ctx.variant === "segmented";

  return (
    <button
      type="button"
      role="tab"
      id={`${ctx.baseId}-tab-${value}`}
      aria-selected={active}
      aria-controls={`${ctx.baseId}-panel-${value}`}
      tabIndex={active ? 0 : -1}
      onClick={() => ctx.setValue(value)}
      className={cn(
        "relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap font-medium",
        "transition-colors duration-[var(--duration-micro)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:pointer-events-none disabled:opacity-45",
        segmented
          ? cn(
              // a pílula fica com 28px; o alvo de toque de 44px vem do hit-44
              "hit-44 h-7 rounded-pill px-3.5 text-xs",
              active
                ? "bg-surface text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground",
            )
          : cn(
              "-mb-px border-b-2 px-0.5 py-2.5 text-sm",
              "focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              // alvo de toque no mobile
              "max-md:min-h-11",
              active
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            ),
        className,
      )}
      {...props}
    >
      {children}
      {count !== undefined && count !== null ? (
        <span
          className={cn(
            "nums text-2xs font-medium",
            segmented && active ? "text-primary-text" : "text-subtle-foreground",
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}

export interface TabsPanelProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
  /** Mantém o painel montado (preserva scroll/estado) — só esconde. */
  keepMounted?: boolean;
}

export function TabsPanel({
  value,
  keepMounted = false,
  className,
  children,
  ...props
}: TabsPanelProps) {
  const ctx = useTabs("TabsPanel");
  const active = ctx.value === value;

  if (!active && !keepMounted) return null;

  return (
    <div
      role="tabpanel"
      id={`${ctx.baseId}-panel-${value}`}
      aria-labelledby={`${ctx.baseId}-tab-${value}`}
      tabIndex={0}
      hidden={!active}
      className={cn("focus-visible:outline-none", className)}
      {...props}
    >
      {children}
    </div>
  );
}
